import React from "react";
import { Card } from "../../../../components/ui/card";
import {
  Sparkles,
  TrendingUp,
  Info,
  CheckCircle2,
  Boxes,
  AlertTriangle,
} from "lucide-react";

const formatParagraphs = (text, emphasis = false) => {
  if (!text) return null;

  const safeText = typeof text === "string" ? text : JSON.stringify(text);

  return safeText
    .split(/\n+/)
    .flatMap((line) => line.split(/(?<=\.)\s+/))
    .filter(Boolean)
    .map((line, i) => (
      <p
        key={i}
        className={`text-sm leading-relaxed mb-2 ${
          emphasis
            ? "text-foreground font-medium"
            : "text-muted-foreground"
        }`}
      >
        {line.trim()}
      </p>
    ));
};

export default function ForecastSummary({
  summary,
  growthRate,
  topDrivers = [],
  actionPlan,
  inventoryNotes,
  minHeight = false,
}) {
  return (
    <Card
      className={`
        p-6 rounded-xl
        border border-red-500/20
        bg-gradient-to-br from-red-500/5 to-background
        shadow-md
        flex flex-col
        h-full
        ${minHeight ? "min-h-[380px]" : ""}
      `}
    >
      {/* ---- Header ---- */}
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle className="w-5 h-5 text-red-400" />
        <h2 className="text-xl font-semibold text-foreground tracking-tight">
          Why These Numbers & Actions Matter
        </h2>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">

        {/* ---- FINAL VERDICT (Decision Risk) ---- */}
        {summary && (
          <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-red-400 mb-2 flex items-center gap-1">
              <AlertTriangle className="w-4 h-4" />
              Decision Risk
            </h3>
            {formatParagraphs(summary, true)}
          </div>
        )}

        {/* ---- Demand & Growth Context ---- */}
        {growthRate && (
          <div className="bg-muted/30 p-4 rounded-lg border border-border/30">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2 flex items-center gap-1">
              <TrendingUp className="w-4 h-4 text-yellow-400" />
              Demand & Growth Context
            </h3>
            {formatParagraphs(growthRate)}
          </div>
        )}

        {/* ---- What Is Driving Demand ---- */}
        {topDrivers.length > 0 && (
          <div className="bg-muted/30 p-4 rounded-lg border border-border/30">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground mb-3 flex items-center gap-1">
              <Info className="w-4 h-4 text-blue-400" />
              Hidden Demand Drivers
            </h3>

            <div className="space-y-2">
              {topDrivers.map((driver, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2 bg-background/60 p-3 rounded-lg border border-border/20"
                >
                  <CheckCircle2 className="w-4 h-4 text-blue-300 mt-1 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {driver}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- Why Doing Nothing Is Risky (Action Justification) ---- */}
        {actionPlan && (
          <div className="bg-amber-500/10 p-4 rounded-lg border border-amber-500/30">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-400 mb-2 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" />
              Why Doing Nothing Is Risky
            </h3>
            {formatParagraphs(actionPlan, true)}
          </div>
        )}

        {/* ---- Operational Constraints ---- */}
        {inventoryNotes && (
          <div className="bg-muted/30 p-4 rounded-lg border border-border/30">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground mb-2 flex items-center gap-1">
              <Boxes className="w-4 h-4 text-amber-400" />
              Operational Constraints
            </h3>
            {formatParagraphs(inventoryNotes)}
          </div>
        )}
      </div>
    </Card>
  );
}